import type { PostgrestError } from "@supabase/supabase-js";
import type { Page, PageRequest, RepositoryResult } from "../contracts";
import { decodeCursor, encodeCursor } from "./cursor";
import { translateSupabaseError } from "./errors";
import { validatePageRequest } from "./repository-base";

export const ID_ORDER = "id.asc";

export const validationFailure = <T>(message: string): RepositoryResult<T> => ({
  ok: false,
  error: { code: "validation_failure", message, retryable: false },
});

export const databaseFailure = <T>(
  error: PostgrestError | Error | unknown,
  fallbackMessage: string,
): RepositoryResult<T> => ({
  ok: false,
  error: translateSupabaseError(error, fallbackMessage),
});

export const notFound = <T>(label: string): RepositoryResult<T> => ({
  ok: false,
  error: { code: "not_found", message: `${label} was not found.`, retryable: false },
});

export function cursorId(
  page: PageRequest,
  order: string = ID_ORDER,
): RepositoryResult<string | undefined> {
  const validPage = validatePageRequest(page);
  if (!validPage.ok) return validPage as RepositoryResult<string | undefined>;
  if (!page.cursor) return { ok: true, value: undefined };
  const decoded = decodeCursor(page.cursor, order, 1);
  if (!decoded.ok) return decoded as RepositoryResult<string | undefined>;
  const id = decoded.value.values[0];
  if (typeof id !== "string" || !id) return validationFailure("The pagination cursor has an invalid ID.");
  return { ok: true, value: id };
}

export function createIdPage<Row extends { id: string }, Value>(
  rows: readonly Row[],
  page: PageRequest,
  mapper: (row: Row) => RepositoryResult<Value> | Value,
  order: string = ID_ORDER,
): RepositoryResult<Page<Value>> {
  const hasNext = rows.length > page.limit;
  const visibleRows = hasNext ? rows.slice(0, page.limit) : [...rows];
  const items: Value[] = [];
  for (const row of visibleRows) {
    const mapped = mapper(row);
    if (typeof mapped === "object" && mapped !== null && "ok" in mapped) {
      const result = mapped as RepositoryResult<Value>;
      if (!result.ok) return result as RepositoryResult<Page<Value>>;
      items.push(result.value);
    } else {
      items.push(mapped as Value);
    }
  }
  return {
    ok: true,
    value: {
      items,
      nextCursor:
        hasNext && visibleRows.length
          ? encodeCursor({ version: 1, order, values: [visibleRows[visibleRows.length - 1].id] })
          : undefined,
    },
  };
}
